import { isTokenExpired } from "@/lib/utils";
import useAuth from "@/store/auth.slice";
import { apiEndpointBaseUrl } from "./constants";

let refreshPromise: Promise<string | null> | null = null;
let currentUserId: number | null = null;
let abortController: AbortController | null = null;
let lastRefreshId: number | null = null;

const refreshToken = async (
  refreshTokenStr: string,
  refreshId: number,
): Promise<string | null> => {
  if (isTokenExpired(refreshTokenStr)) {
    return null;
  }

  // Create new abort controller for this request
  if (abortController) {
    abortController.abort();
  }
  abortController = new AbortController();
  const userId = useAuth.getState().user?.id;

  try {
    const response = await fetch(`${apiEndpointBaseUrl}/auth/token/refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh: refreshTokenStr }),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    // Check if this refresh is still valid for the current user
    if (lastRefreshId !== refreshId || currentUserId !== userId) {
      console.log("User or refresh ID changed during refresh, cancelling");
      return null;
    }

    const body = await response.json();

    // Verify the user is still the same before updating state
    if (useAuth.getState().user?.id === userId) {
      useAuth.setState({
        accessToken: body.access,
        refreshToken: body.refresh,
      });
      return body.access;
    }
    return null;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return null;
    }
    // Only log error if it's not during initial login
    const isInitialLogin = !useAuth.getState().loggedIn;
    if (!isInitialLogin) {
      console.error("Error refreshing token:", error);
    }

    return null;
  } finally {
    if (lastRefreshId === refreshId && currentUserId === userId) {
      abortController = null;
    }
  }
};

const clearRefreshState = () => {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
  refreshPromise = null;
  currentUserId = null;
  lastRefreshId = null;
};

/**
 * POST /auth/token/refresh/ with module-level deduplication (same in-flight promise
 * for concurrent callers). Used when the access token is expired or may still be valid
 * but must be rotated (proactive timer fires before JWT `exp`).
 */
export const refreshAccessTokenShared = async (): Promise<string | null> => {
  const userId = useAuth.getState().user?.id;
  const refresh = useAuth.getState().refreshToken || "";
  if (!refresh || isTokenExpired(refresh)) {
    return null;
  }

  // Cancel existing refresh if user changed
  if (refreshPromise && currentUserId !== userId) {
    console.log(
      `User changed from ${currentUserId} to ${userId}, clearing refresh state`,
    );
    clearRefreshState();
  }

  // If there's already a refresh in progress for the same user, return its promise
  if (refreshPromise && currentUserId === userId) {
    return refreshPromise;
  }

  const refreshId = Date.now();
  currentUserId = userId;
  lastRefreshId = refreshId;

  // Store the promise before making the request. Reset after completion so the
  // next expiry creates a fresh refresh call instead of returning the stale value.
  refreshPromise = refreshToken(refresh, refreshId).finally(() => {
    if (lastRefreshId === refreshId) {
      refreshPromise = null;
    }
  });
  return refreshPromise;
};

/**
 * Returns the access JWT when still valid; otherwise {@link refreshAccessTokenShared}.
 */
export const getAuthToken = async (
  token = useAuth.getState().accessToken || "",
): Promise<string | null> => {
  if (token && !isTokenExpired(token)) return token;

  return refreshAccessTokenShared();
};
